import { useState, useCallback, useEffect, useRef } from 'react';
import { SessionMemoryEntry, SessionMemorySnapshot } from '@/types/sessionMemory.types';
import type { VocalPack } from '@/types';
import { useVocalPacks } from './useVocalPacks';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const STORAGE_KEY = 'hookhouse_session_memories';
const API_BASE = '/api/session-memories';

function generateId(): string {
  return `sm_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
}

function loadFromStorage(): SessionMemoryEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// saveToStorage removed — DB is the source of truth.
// loadFromStorage() below is kept as a read-only fallback for initial load.

function buildPreview(snapshot: SessionMemorySnapshot, vocalPacks: Record<string, VocalPack>): SessionMemoryEntry['preview'] {
  const vocalConfig = snapshot.templateConfig.vocalConfig;
  let vocalPackName = 'None';
  if (vocalConfig.mode === 'blend' && vocalConfig.blendOutput) {
    vocalPackName = vocalConfig.blendOutput.name;
  } else if (vocalConfig.singlePackKey) {
    const pack = vocalPacks[vocalConfig.singlePackKey];
    vocalPackName = pack?.name || vocalConfig.singlePackKey;
  }

  return {
    vocalPackName,
    blends: snapshot.templateConfig.musicalBlend || [],
    moods: snapshot.templateConfig.moodArchitecture || [],
    seedPreview: (snapshot.formData.seed || '').substring(0, 100),
    hasOutput: !!snapshot.lastOutput
  };
}

export function useSessionMemory() {
  const { data: vocalPacks } = useVocalPacks();
  const [entries, setEntries] = useState<SessionMemoryEntry[]>([]);
  const initialized = useRef(false);

  // Load from API on mount, fall back to localStorage
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        const res = await fetchWithCsrf(`${API_BASE}/list`);
        if (res.ok) {
          const json = await res.json();
          if (json.data && json.data.length > 0) {
            setEntries(json.data);
            return;
          }
        }
      } catch {
        // API unavailable — fall through to localStorage
      }
      // Fallback: load from localStorage
      setEntries(loadFromStorage());
    })();
  }, []);

  const saveSession = useCallback((
    name: string,
    description: string,
    icon: string,
    tags: string[],
    snapshot: SessionMemorySnapshot
  ): SessionMemoryEntry => {
    const preview = buildPreview(snapshot, vocalPacks);
    const entry: SessionMemoryEntry = {
      id: generateId(),
      name,
      description,
      icon,
      tags,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      snapshot,
      preview
    };

    // Optimistic: update local state immediately
    setEntries(prev => [entry, ...prev]);

    // Persist to API in background
    fetchWithCsrf(`${API_BASE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description, icon, tags, snapshot, preview }),
    }).then(async (res) => {
      if (res.ok) {
        const json = await res.json();
        // Update the optimistic entry with the server-assigned id
        setEntries(prev => prev.map(e =>
          e.id === entry.id ? { ...e, id: json.data.id } : e
        ));
      }
    }).catch(() => {
      // API failed — entry stays in local state with client-generated ID
    });

    return entry;
  }, [vocalPacks]);

  const deleteSession = useCallback((id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));

    fetchWithCsrf(`${API_BASE}/${id}`, { method: 'DELETE' }).catch(() => {
      // Silently fail — already removed locally
    });
  }, []);

  const updateSession = useCallback((id: string, updates: { name?: string; description?: string; icon?: string; tags?: string[] }) => {
    setEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      return {
        ...e,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    }));

    fetchWithCsrf(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    }).catch(() => {
      // Silently fail — already updated locally
    });
  }, []);

  const exportSessions = useCallback((): string => {
    return JSON.stringify({
      hookhouse_sessions_v1: {
        exportedAt: new Date().toISOString(),
        sessions: entries
      }
    }, null, 2);
  }, [entries]);

  const importSessions = useCallback((json: string): number => {
    const data = JSON.parse(json);
    const imported: SessionMemoryEntry[] = data.hookhouse_sessions_v1?.sessions || [];
    if (imported.length === 0) return 0;

    const existingIds = new Set(entries.map(e => e.id));
    const newEntries = imported.filter(e => !existingIds.has(e.id));

    setEntries(prev => [...newEntries, ...prev]);

    // Persist each imported entry to API in background
    for (const entry of newEntries) {
      fetchWithCsrf(`${API_BASE}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: entry.name,
          description: entry.description,
          icon: entry.icon,
          tags: entry.tags,
          snapshot: entry.snapshot,
          preview: entry.preview,
        }),
      }).catch(() => {});
    }

    return newEntries.length;
  }, [entries]);

  return {
    entries,
    saveSession,
    deleteSession,
    updateSession,
    exportSessions,
    importSessions
  };
}
